import styled from "styled-components";

// Typography from:
// https://www.figma.com/design/gxBFoxXt4DH2ooeq6Vrv7b/Foundation?node-id=4-2360&m=dev

interface TypographyProps {
  color?: string;
  isStrong?: boolean;
  rewardsBanner?: boolean;
}

// Heading
export const H900 = styled.div<TypographyProps>`
  color: ${({ theme, color }) => theme.colors[color] || theme.colors.default};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 32px;
  font-style: normal;
  line-height: 40px;
  letter-spacing: -0.64px;

  // Medium up (Tablet)
  ${({ theme }) => theme.up.md`
    font-size: 66px;
    line-height: 72px;
    letter-spacing: -1.32px;
  `}
`;

export const H800 = styled.div<TypographyProps>`
  color: ${({ theme, color }) => theme.colors[color] || theme.colors.default};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 29px;
  font-style: normal;
  font-weight: 300;
  line-height: 36px;
  letter-spacing: -0.58px;

  // Medium up (Tablet)
  ${({ theme }) => theme.up.md`
    font-size: 52px;
    line-height: 56px;
    letter-spacing: -1.04px;
  `}
`;

export const H700 = styled.div<TypographyProps>`
  color: ${({ theme, color }) => theme.colors[color] || theme.colors.default};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 26px;
  font-style: normal;
  font-weight: 300;
  line-height: 32px;
  letter-spacing: -0.26px;

  // Large up (Laptop)
  ${({ theme }) => theme.up.lg`
    font-size: 41px;
    line-height: 48px;
    letter-spacing: -0.82px;
  `}
`;

export const H600 = styled.div<TypographyProps>`
  color: ${({ theme, color }) => theme.colors[color] || theme.colors.default};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 23px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  letter-spacing: -0.23px;

  // Large up (Laptop)
  ${({ theme }) => theme.up.lg`
    font-size: 32px;
    font-weight: 300;
    line-height: 40px;
    letter-spacing: -0.64px;
  `}
`;

export const H500 = styled.div<TypographyProps>`
  color: ${({ theme, color }) => theme.colors[color] || theme.colors.default};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0px;

  // Medium up (Tablet)
  ${({ theme }) => theme.up.md`
    font-size: 26px;
    font-weight: 400;
    line-height: 32px;
    letter-spacing: -0.26px;
  `}
`;

export const H400 = styled.div<TypographyProps>`
  color: ${({ theme, color }) => theme.colors[color] || theme.colors.default};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: -0.09px;

  // Large up (Laptop)
  ${({ theme }) => theme.up.lg`
    font-size: 20px;
    line-height: 24px;
    letter-spacing: -0.1px;
  `}
`;

export const H300 = styled.div<TypographyProps>`
  color: ${({ theme, color }) => theme.colors[color] || theme.colors.default};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: -0.08px;
`;

// Text
export const T400 = styled.div<TypographyProps>`
  color: ${({ theme, color }) => theme.colors[color] || theme.colors.default};
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: 16px;
  font-style: normal;
  font-weight: ${({ isStrong }) => (isStrong ? 600 : 400)};
  line-height: 32px;

  // Large up (Laptop)
  ${({ theme }) => theme.up.lg`
    font-size: 20px;
    line-height: 24px;
  `}
`;

export const T300 = styled.div<TypographyProps>`
  color: ${({ theme, color, rewardsBanner }) => rewardsBanner ? "#fafafad6" : theme.colors[color] || theme.colors.default};
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: 14px;
  font-style: normal;
  font-weight: ${({ isStrong }) => (isStrong ? 600 : 400)};
  line-height: 20px;

  // Large up (Laptop)
  ${({ theme }) => theme.up.lg`
    font-size: 16px;
    line-height: 24px;
  `}
`;

export const T200 = styled.div<TypographyProps>`
  color: ${({ theme, color }) => theme.colors[color] || theme.colors.default};
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: 14px;
  font-style: normal;
  font-weight: ${({ isStrong }) => (isStrong ? 600 : 400)};
  line-height: 20px;
`;

export const T100 = styled.div<TypographyProps>`
  color: ${({ theme, color }) => theme.colors[color] || theme.colors.default};
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: 12px;
  font-style: normal;
  font-weight: ${({ isStrong }) => (isStrong ? 600 : 400)};
  line-height: 16px;
`;

// Label
export const L400 = styled.div<TypographyProps>`
  color: ${({ theme, color }) => theme.colors[color] || theme.colors.default};
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.2px;
`;

export const L300 = styled.div<TypographyProps>`
  color: ${({ theme, color }) => theme.colors[color] || theme.colors.default};
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.16px;
`;

export const L200 = styled.div<TypographyProps>`
  color: ${({ theme, color }) => theme.colors[color] || theme.colors.default};
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.07px;
`;

export const L100 = styled.div<TypographyProps>`
  color: ${({ theme, color }) => theme.colors[color] || theme.colors.default};
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: 12px;
  font-style: normal;
  font-weight: ${({ isStrong }) => (isStrong ? 700 : 500)};
  line-height: 16px;
  letter-spacing: 0.06px;
`;
